import React from "react";
import type { FC, ReactNode } from "react";

import { SubscriptionsSettingsSchedule } from "../SubscriptionsSettings/SubscriptionsSettingsSchedule";
import { SubscriptionsSettingsName } from "../SubscriptionsSettings/SubscriptionsSettingsName";
import { SubscriptionsSettingsFileFormat } from "../SubscriptionsSettings/SubscriptionsSettingsFileFormat";
import { SubscriptionsSettingsSection } from "../SubscriptionsSettings/SubscriptionsSettingsSection";
import { SubscriptionsSettingsSlide } from "../SubscriptionsSettings/SubscriptionsSettingsSlide";
import { messages } from "../SubscriptionsSettings/SubscriptionsSettings.messages";

import { SubscriptionsDialog } from "../SubscriptionsDialog";
import { SubscriptionItemType } from "../../types";
import { useSubscriptionEditProviderContext, SubscriptionEditProvider } from "../../providers";

import { AddSubscriptionDialogProps } from "./AddSubscriptionDialog.types";
import { useStyles } from "./AddSubscriptionDialog.styles";
import dialogMessages from "./AddSubscriptionDialog.messages";

const AddSubscriptionDialogContent: FC<AddSubscriptionDialogProps> = (props) => {
  const { type, payload, ...otherProps } = props;
  const classes = useStyles();

  const {
    state: { currentSubscriptionItem, validationErrors, isAddDisabled },
    actions: { setSubscriptionPropertyValue, addSubscription, setApplyPayloadFunction },
  } = useSubscriptionEditProviderContext();

  let settings: ReactNode = null;
  switch (currentSubscriptionItem.type) {
    case SubscriptionItemType.Slide:
      settings = (
        <SubscriptionsSettingsSection label={messages.selections} className={classes.slideSelection}>
          <SubscriptionsSettingsSlide
            value={currentSubscriptionItem.payload}
            onChange={setSubscriptionPropertyValue("payload")}
            setApplyPayloadFunction={setApplyPayloadFunction}
          />
        </SubscriptionsSettingsSection>
      );
      break;
    default:
      settings = null;
  }

  return (
    <SubscriptionsDialog
      className={classes.paper}
      title={dialogMessages.title}
      actions={[{ name: dialogMessages.save, handleClick: addSubscription, disabled: isAddDisabled }, { name: dialogMessages.cancel }]}
      alignActions="left"
      {...otherProps}
    >
      <div className={classes.settingsContainer}>
        <SubscriptionsSettingsName
          value={currentSubscriptionItem.name}
          error={validationErrors?.name}
          onChange={setSubscriptionPropertyValue("name")}
        />

        {settings}
        <SubscriptionsSettingsSection label={messages.fileFormat}>
          <SubscriptionsSettingsFileFormat
            value={currentSubscriptionItem.fileFormat}
            type={currentSubscriptionItem.type}
            onChange={setSubscriptionPropertyValue("fileFormat")}
          />
        </SubscriptionsSettingsSection>

        <SubscriptionsSettingsSection label={messages.frequencyParameters}>
          <SubscriptionsSettingsSchedule value={currentSubscriptionItem.schedule} onChange={setSubscriptionPropertyValue("schedule")} />
        </SubscriptionsSettingsSection>
      </div>
    </SubscriptionsDialog>
  );
};

export const AddSubscriptionDialog: FC<AddSubscriptionDialogProps> = (props) => {
  if (!props.open) {
    return null;
  }

  return (
    <SubscriptionEditProvider {...props}>
      <AddSubscriptionDialogContent {...props} />
    </SubscriptionEditProvider>
  );
};
